import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { fetchUsers } from '../../store/thunks/userThunk';
import { addStaffToClinic } from '../../store/thunks/clinicThunk';
import { logInfo, logError } from '../../utils/logger';

const AddStaffToClinicModal = ({ isOpen, onClose, clinicId, existingStaff = [] }) => {
    const dispatch = useDispatch();
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [saveStatus, setSaveStatus] = useState('initial');
    const { users = [], status: usersStatus, error: usersError } = useSelector((state) => state.users);

    useEffect(() => {
        if (isOpen) {
            dispatch(fetchUsers())
                .unwrap()
                .then(() => {
                    logInfo('Users fetched successfully for staff selection');
                })
                .catch((error) => {
                    logError('Failed to fetch users for staff selection', { error });
                });
        }
    }, [dispatch, isOpen]);

    const availableStaff = users.filter(user => 
        !existingStaff.some(staff => staff._id === user._id)
    );

    const staffOptions = availableStaff.map(user => ({
        value: user._id,
        label: `${user.firstName} ${user.lastName} (${user.email})`
    }));

    const handleAddStaff = async () => {
        if (selectedStaff) {
            setSaveStatus('saving');
            try {
                await dispatch(addStaffToClinic({ clinicId, staffId: selectedStaff.value })).unwrap();
                logInfo('Staff added to clinic successfully', { clinicId, staffId: selectedStaff.value });
                setSaveStatus('saved');
                setTimeout(() => {
                    onClose();
                    setSelectedStaff(null);
                    setSaveStatus('initial');
                }, 1000);
            } catch (error) {
                logError('Failed to add staff to clinic', { error, clinicId, staffId: selectedStaff.value });
                setSaveStatus('error');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-sm shadow-xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add Staff to Clinic</h3>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-sm transition-colors"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-4">
                    {usersStatus === 'loading' && (
                        <div className="text-center py-4">
                            <div className="text-sm text-gray-600">Loading available staff...</div>
                        </div>
                    )}
                    
                    {usersError && (
                        <div className="text-center py-4">
                            <div className="text-sm text-red-600">Error: {usersError}</div>
                        </div>
                    )}
                    
                    {availableStaff.length === 0 && usersStatus !== 'loading' && (
                        <div className="text-center py-4">
                            <div className="text-sm text-gray-600">No available staff to add.</div>
                        </div>
                    )}
                    
                    {availableStaff.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Staff Member
                            </label>
                            <Select
                                value={selectedStaff}
                                onChange={setSelectedStaff}
                                options={staffOptions}
                                className="w-full"
                                placeholder="Select a staff member"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: '0.125rem',
                                        borderColor: '#d1d5db',
                                        '&:hover': {
                                            borderColor: '#9ca3af'
                                        },
                                        '&:focus': {
                                            borderColor: '#111827',
                                            boxShadow: '0 0 0 2px rgba(17, 24, 39, 0.2)'
                                        }
                                    })
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAddStaff}
                        disabled={!selectedStaff || saveStatus === 'saving'}
                        className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                            saveStatus === 'saving'
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : saveStatus === 'saved'
                                ? 'bg-green-600 text-white'
                                : saveStatus === 'error'
                                ? 'bg-red-600 text-white'
                                : !selectedStaff
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                    >
                        {saveStatus === 'saving' ? 'Adding...' :
                         saveStatus === 'saved' ? 'Added!' :
                         saveStatus === 'error' ? 'Error' : 'Add Staff'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddStaffToClinicModal;
